import React, { useState, useEffect } from "react";
import {
  FaPowerOff,
  FaLightbulb,
  FaPlus,
  FaMinus,
  FaPlug,
  FaEdit,
} from "react-icons/fa";
import styles from "../../pages/Monitor/Monitor.module.css";

const normalizeVisibleValue = (value) => {
  if (value === undefined || value === null) return true;

  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    value === "true" ||
    value === "TRUE"
  );
};

const DigitalInputCard = ({
  type,
  index,
  label,
  icon,
  data,
  onUpdate,
  customNames = {},
  onSaveCustomName,

  showVisibilityToggle = false,
  isVisibleInMonitor = true,
  onVisibilityChange,
  canConfigure = true,
  readOnlyPreview = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");

  const [localVisibleInMonitor, setLocalVisibleInMonitor] = useState(
    normalizeVisibleValue(isVisibleInMonitor),
  );
  const [isSavingVisibility, setIsSavingVisibility] = useState(false);

  useEffect(() => {
    setLocalVisibleInMonitor(normalizeVisibleValue(isVisibleInMonitor));
  }, [isVisibleInMonitor]);

  const handleVisibilityToggle = async (e) => {
    const nextValue = e.target.checked;
    const previousValue = localVisibleInMonitor;

    setLocalVisibleInMonitor(nextValue);

    if (isReadOnlyPreview || !onVisibilityChange) return;

    try {
      setIsSavingVisibility(true);

      const result = onVisibilityChange(nextValue);

      if (result && typeof result.then === "function") {
        await result;
      }
    } catch (error) {
      console.error("خطا در تغییر وضعیت نمایش در مانیتورینگ:", error);
      setLocalVisibleInMonitor(previousValue);
      alert("خطا در ذخیره وضعیت نمایش");
    } finally {
      setIsSavingVisibility(false);
    }
  };

  if (!data) return null;

  const isReadOnlyPreview = Boolean(
    readOnlyPreview ||
    data?.isOfflinePreview ||
    data?.isLastStatePreview ||
    typeof onUpdate !== "function" ||
    !canConfigure,
  );

  const safeUpdate = (prop, value) => {
    if (isReadOnlyPreview || typeof onUpdate !== "function") return undefined;
    return onUpdate(prop, value);
  };

  const { status, settings = {}, triggered } = data;
  const isOn = status === "1";

  // دریافت نام سفارشی برای این ورودی خاص
  const customName = customNames[index] ?? customNames[String(index)];

  const activeRelays = settings?.toDOut
    ? settings.toDOut
        .split(",")
        .map(Number)
        .filter((n) => !isNaN(n))
    : [];

  const toggleRelay = (relayId) => {
    let newRelays;

    if (activeRelays.includes(relayId)) {
      newRelays = activeRelays.filter((r) => r !== relayId);
    } else {
      newRelays = [...activeRelays, relayId].sort((a, b) => a - b);
    }

    safeUpdate("toDOut", newRelays.join(","));
  };

  const handleKeyDown = (e, prop) => {
    if (e.key === "Enter") {
      safeUpdate(prop, e.target.value);
      e.target.blur();
    }
  };

  // ذخیره نام سفارشی
  const handleSaveName = () => {
    if (!isReadOnlyPreview && onSaveCustomName) {
      onSaveCustomName(index, editedName.trim());
    }

    setIsEditingName(false);
  };

  // شروع ویرایش نام
  const startEditingName = (e) => {
    e.stopPropagation();
    if (isReadOnlyPreview) return;
    setEditedName(customName || "");
    setIsEditingName(true);
  };

  const relayCount = isExpanded ? 72 : 8;
  const relayList = [...Array(relayCount).keys()];

  return (
    <div
      className={`${styles.sensorCard} ${isReadOnlyPreview ? styles.configOfflinePreviewCard : ""}`}
      style={{ borderRight: "4px solid #f6ad55" }}
    >
      {isReadOnlyPreview && (
        <div className={styles.configOfflinePreviewTag}>
          آخرین کانفیگ ذخیره‌شده
        </div>
      )}
      {/* --- هدر (بدون چراغ) --- */}
      <div className={styles.cardHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {icon}

          <span style={{ fontWeight: "bold" }}>
            {/* نمایش نام سفارشی یا نام پیش‌فرض */}
            {customName ? (
              <div
                style={{ display: "flex", flexDirection: "column", gap: "2px" }}
              >
                <span style={{ fontSize: "14px", color: "#f1c40f" }}>
                  {customName}
                </span>

                <span style={{ fontSize: "10px", color: "#7f8c8d" }}>
                  {label}
                </span>
              </div>
            ) : (
              label
            )}
          </span>
        </div>

        {/* دکمه ویرایش نام */}
        <div
          onClick={startEditingName}
          style={{ cursor: "pointer", padding: "5px", color: "#3498db" }}
          title="ویرایش نام"
        >
          <FaEdit style={{ opacity: isReadOnlyPreview ? 0.35 : 1 }} />
        </div>
      </div>

      {/* مدال ویرایش نام */}
      {isEditingName && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#2c3e50",
            padding: "15px",
            borderRadius: "8px",
            zIndex: 100,
            boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
            border: "1px solid rgba(255,255,255,0.1)",
            minWidth: "200px",
          }}
        >
          <div
            style={{ marginBottom: "10px", fontSize: "12px", color: "#ccc" }}
          >
            ویرایش نام ورودی {index}
          </div>

          <input
            type="text"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            placeholder="نام جدید را وارد کنید..."
            style={{
              width: "100%",
              padding: "8px 0px",
              borderRadius: "4px",
              border: "1px solid #34495e",
              backgroundColor: "#1a252f",
              color: "rgb(245, 196, 5)",
              marginBottom: "10px",
            }}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSaveName();
              if (e.key === "Escape") setIsEditingName(false);
            }}
          />

          <div
            style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}
          >
            <button
              onClick={() => setIsEditingName(false)}
              style={{
                padding: "5px 10px",
                backgroundColor: "#7f8c8d",
                border: "none",
                borderRadius: "4px",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              لغو
            </button>

            <button
              onClick={handleSaveName}
              style={{
                padding: "5px 10px",
                backgroundColor: "#2ecc71",
                border: "none",
                borderRadius: "4px",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              ذخیره
            </button>
          </div>
        </div>
      )}

      {/* --- وضعیت مرکزی --- */}
      <div className={styles.centerStatus}>
        <div
          style={{
            position: "relative",
            width: "50px",
            height: "50px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <FaPlug
            className={`${styles.powerIcon} ${isOn ? styles.inputActive : styles.inputInactiveBase}`}
            style={{ transform: "rotate(180deg)" }}
          />

          {!isOn && <div className={styles.diagonalLine}></div>}
        </div>

        <div className={styles.statusText}>
          {isOn ? "منبع روشن" : "منبع خاموش"}
        </div>
      </div>

      {/* --- گروه تنظیمات (با چراغ و افکت نور) --- */}
      <div
        className={styles.settingsGroup}
        style={{ position: "relative", overflow: "hidden" }}
      >
        {/* افکت نور پس‌زمینه */}
        {triggered && <div className={styles.triggerGlow}></div>}

        {/* آیکون چراغ در پایین سمت چپ */}
        <div className={styles.triggerIconContainer}>
          <FaLightbulb
            className={`${styles.trigIcon} ${triggered ? styles.trigOn : styles.trigOff}`}
            style={{ fontSize: "18px", transform: "rotate(180deg)" }}
          />

          {triggered && <div className={styles.blinkingTooltip}>Trig فعال</div>}
        </div>

        {showVisibilityToggle && (
          <div className={styles.settingRow}>
            <span>
              نمایش در مانیتورینگ
              {isSavingVisibility && (
                <small style={{ marginRight: "6px", color: "#95a5a6" }}>
                  در حال ذخیره...
                </small>
              )}
            </span>

            <label
              className={styles.toggleSwitch}
              title={
                localVisibleInMonitor
                  ? "در مانیتورینگ نمایش داده می‌شود"
                  : "در مانیتورینگ مخفی است"
              }
            >
              <input
                type="checkbox"
                checked={localVisibleInMonitor}
                disabled={isSavingVisibility || isReadOnlyPreview}
                onChange={handleVisibilityToggle}
              />

              <span className={styles.slider}></span>
            </label>
          </div>
        )}

        <div className={styles.settingRow}>
          <span>تاخیر وصل (Lead):</span>

          <input
            className={styles.inputNum}
            defaultValue={settings.leadDelay}
            disabled={isReadOnlyPreview}
            onBlur={(e) => safeUpdate("leadDelay", e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, "leadDelay")}
            placeholder="0-4M"
          />
        </div>

        <div className={styles.settingRow}>
          <span>تاخیر قطع (Lag):</span>

          <input
            className={styles.inputNum}
            defaultValue={settings.lagDelay}
            disabled={isReadOnlyPreview}
            onBlur={(e) => safeUpdate("lagDelay", e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, "lagDelay")}
            placeholder="0-4M"
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "10px",
          }}
        >
          <div
            className={styles.settingRow}
            style={{ border: "none", padding: 0 }}
          >
            <span style={{ fontSize: "12px" }}>معکوس:</span>

            <label className={styles.toggleSwitch}>
              <input
                type="checkbox"
                checked={settings.reverse === "1"}
                disabled={isReadOnlyPreview}
                onChange={(e) =>
                  safeUpdate("reverse", e.target.checked ? "1" : "0")
                }
              />
              <span className={styles.slider}></span>
            </label>
          </div>

          <div
            className={styles.settingRow}
            style={{ border: "none", padding: 0 }}
          >
            <span style={{ fontSize: "12px" }}>پیامک:</span>

            <label className={styles.toggleSwitch}>
              <input
                type="checkbox"
                checked={settings.alertSms === "1"}
                disabled={isReadOnlyPreview}
                onChange={(e) =>
                  safeUpdate("alertSms", e.target.checked ? "1" : "0")
                }
              />
              <span className={styles.slider}></span>
            </label>
          </div>
        </div>

        <div
          className={styles.settingRow}
          style={{
            flexDirection: "column",
            alignItems: "flex-start",
            borderTop: "1px solid #ffffff10",
            paddingTop: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <span style={{ fontSize: "13px", color: "#3498db" }}>
              خروجی‌های متصل ({isExpanded ? "۰-۷۱" : "۰-۷"}):
            </span>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "3px",
              padding: "6px",
              background: "rgba(0,0,0,0.2)",
              borderRadius: "4px",
              width: "100%",
              border: "1px solid rgba(255,255,255,0.05)",
            }}
          >
            {relayList.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => toggleRelay(r)}
                disabled={isReadOnlyPreview}
                style={{
                  width: "22px",
                  height: "22px",
                  fontSize: "9px",
                  backgroundColor: activeRelays.includes(r)
                    ? "#2ecc71"
                    : "#34495e",
                  border: "none",
                  color: "white",
                  borderRadius: "3px",
                  cursor: "pointer",
                }}
              >
                {r}
              </button>
            ))}

            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              style={{
                width: "22px",
                height: "22px",
                background: "#e67e22",
                color: "white",
                border: "none",
                borderRadius: "3px",
                fontSize: "10px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isExpanded ? <FaMinus /> : <FaPlus />}
            </button>
          </div>

          {activeRelays.length > 0 && (
            <div
              style={{ fontSize: "10px", color: "#95a5a6", marginTop: "5px" }}
            >
              انتخاب شده: {activeRelays.join(", ")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DigitalInputCard;
